import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, Flame, Activity } from 'lucide-react';
import { Badge } from './ui/badge';

const ENGINE_LABELS = {
  consenso: 'Consenso',
  prometeo: 'Prometeo',
  seven_ia: '7IA',
  tauro: 'Tauro',
};

export const RachaPanel = ({ engine, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/racha/${engine}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setData(res.data);
    } catch (e) { /* silencioso */ }
    setLoading(false);
  }, [engine, token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (loading) {
    return (
      <div className="mb-5 p-4 rounded-xl bg-slate-900/50 border border-slate-700/40 flex items-center gap-2"
        data-testid={`racha-panel-loading-${engine}`}>
        <Loader2 className="animate-spin text-emerald-400" size={16} />
        <span className="text-slate-400 text-xs">Calculando racha real de {ENGINE_LABELS[engine]} contra sorteos reales...</span>
      </div>
    );
  }

  if (!data?.lotteries?.length) return null;

  const rows = showAll ? data.lotteries : data.lotteries.slice(0, 5);
  const best = data.best;

  return (
    <div className="mb-5 rounded-xl bg-gradient-to-br from-emerald-950/60 to-slate-900/60 border border-emerald-600/30 overflow-hidden"
      data-testid={`racha-panel-${engine}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Activity className="text-emerald-400" size={16} />
            <h3 className="text-white font-black text-sm uppercase tracking-wide">Racha Real — ¿Dónde está acertando {ENGINE_LABELS[engine]}?</h3>
          </div>
          <Badge variant="outline" className="border-emerald-600/40 text-emerald-300 text-[9px]">
            Backtest verificado · sin IA
          </Badge>
        </div>
        <p className="text-slate-400 text-[11px] mb-3">
          Cada lotería se prueba contra sus últimos sorteos REALES. Juega donde el método está en racha.
        </p>

        {best && (
          <div className="mb-3 p-3 rounded-lg bg-emerald-900/30 border border-emerald-500/40 flex items-center gap-3"
            data-testid={`racha-best-${engine}`}>
            <Flame className="text-orange-400 shrink-0" size={20} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white font-black text-sm">{best.lottery_name}</span>
                {best.en_racha && (
                  <Badge className="bg-orange-500/20 border border-orange-400/50 text-orange-300 text-[9px] font-black uppercase">🔥 En racha</Badge>
                )}
              </div>
              <div className="text-emerald-300/80 text-[10px]">
                Mejor opción hoy · {best.any_rate}% acierto real en {best.draws_tested} sorteos · racha {best.recent_hits_5}/5
              </div>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          {rows.map((r, idx) => (
            <div key={r.lottery_id} className="flex items-center gap-2 p-2 rounded-lg bg-black/30 border border-slate-700/30"
              data-testid={`racha-row-${engine}-${idx}`}>
              <span className="text-slate-500 text-[10px] font-bold w-4">{idx + 1}</span>
              <div className="w-1 h-6 rounded-full shrink-0" style={{ backgroundColor: r.color || '#10b981' }} />
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-bold truncate flex items-center gap-1.5">
                  {r.lottery_name}
                  {r.en_racha && <span className="text-orange-400 text-[9px]">🔥</span>}
                </div>
                <div className="text-slate-500 text-[9px]">{r.draw_time} · pool {r.pool_size} números</div>
              </div>
              <div className="flex gap-0.5">
                {r.hits_series.slice(0, 8).map((h, i) => (
                  <span key={i} className={`w-3.5 h-3.5 rounded-full text-[7px] font-black flex items-center justify-center ${
                    h ? 'bg-emerald-500/90 text-white' : 'bg-slate-700/80 text-slate-500'
                  }`}>{h ? '✓' : '✗'}</span>
                ))}
              </div>
              <div className="text-right w-14">
                <div className={`font-black text-sm ${r.any_rate >= 30 ? 'text-emerald-400' : r.any_rate >= 15 ? 'text-amber-400' : 'text-slate-400'}`}>
                  {r.any_rate}%
                </div>
                <div className="text-slate-500 text-[8px] uppercase">real</div>
              </div>
            </div>
          ))}
        </div>

        {data.lotteries.length > 5 && (
          <button onClick={() => setShowAll(!showAll)}
            className="mt-2 w-full text-center text-emerald-400 text-[11px] font-bold hover:text-emerald-300 transition-colors"
            data-testid={`racha-toggle-${engine}`}>
            {showAll ? 'Ver menos' : `Ver las ${data.lotteries.length} loterías`}
          </button>
        )}
      </div>
    </div>
  );
};
